/* Серверный режим экрана «Коллаж из фото» (collage) на движке шаблона (план вехи 3, lane B; collage-slides §2.1).
   createCollage(createCollageServer) (features/collage/engine.ts) отдаёт сюда крючки экрана, а каждая вставка SV спрашивает этот объект:
   цена, готовность, свои фото, создание, хвост и итог берутся только из сервиса заказа кадра (OrderService привязки EngineFrame).
   Правила:
   - одна привязка — один кадр ServerStudio (ключ кадра и его сервис заказа): у другого кадра — и у нового сервиса под прежним ключом —
     своё состояние экрана (S), свой мост загрузок и свой хвост BotRun, поэтому фото, согласие и заказ одного приложения или владельца
     не видны и не уходят под другим;
   - движок не пишет в sessionStorage и не зовёт создание сам: заявку, ключ, отправку, опрос и отказы ведёт сервис (run-driver.ts);
   - S → входы — одна точка записи (inputs-mirror; зеркало зовут syncFoot и pvPaint движка): те же ключи и кодирование, что у родной
     формы (collage-inputs.ts, CollageOrderForm.tsx); входы, изменённые не зеркалом (повтор из «Истории», заявка родной формы), — в S;
   - свои фото — лоток моста загрузок (media-bridge: слоты формы photo_01…photo_09 или JSON-список, согласие первым, одна очередь,
     «Продолжить загрузку» тем же id, окончательный отказ убирает файл); перестановка и «Перемешать» меняют порядок записей лотка
     (ключи слотов переписываются без новой загрузки); S.files — только записи моста;
   - LAYS, FMTS, GAPS и BGS сужаются на месте до опубликованных значений (полные списки шаблона сервер помнит сам);
   - примеры без своих фото — из документа экрана (GET /app/studio/screens/collage: examples, Q-CS5), чипы подписи — оттуда же;
     картинок прототипа (PICS, BC.YOU_F) в серверном режиме нет (D12);
   - цена на кнопке — сервиса; скидка «дешевле» — только если форма её публикует и она посчитана для этой же цены (D11);
     тексты — словарь профиля в момент показа (uiText, uiFormat). */
import { endpoints } from '../../api';
import type { SpeechOrder } from '../../api/speech';
import { accountLocale, uiFormat, uiText } from '../../i18n';
import { circleBalance, forgetCircleBalance, rememberCircleBalance, toolPrice } from '../studio/circle/tool-helpers';
import { tailCurrent, tailSteps } from '../studio/circle/order-tail-model';
import { studioCheapOffer, type StudioApp, type StudioForm } from '../studio/studio-api';
import { safeMediaUrl } from '../studio/studio-content';
import { esc, failText, receipt, serverFoot, type BotRunApi, type BotRunRun } from '../studio/engine-kit/bot-run-server';
import { openConsentSheet, type ConsentSheet } from '../studio/engine-kit/consent-sheet';
import { ENGINES, type EngineBinding, type EngineBound, type EngineScreen } from '../studio/engine-kit/engine-host';
import { createInputsMirror, sameInputs, type InputsMirror } from '../studio/engine-kit/inputs-mirror';
import { createMediaBridge, mediaPolicy, type MediaBridge, type MediaItem, type MediaTray } from '../studio/engine-kit/media-bridge';
import { uploadFailureText, type Intent, type OrderService } from '../studio/engine-kit/order-service';
import { createRunDriver, peekIntent, type RunDriver } from '../studio/engine-kit/run-driver';
import { screenDoc, type ScreenDoc } from '../studio/engine-kit/screen-doc';
import { poorBalance, serverUi, type ServerUi } from '../studio/engine-kit/server-ui';
import { archive, archiveUrl, createResultGallery, galleryKey, ORDER_IMAGE_LIMITS, type GalleryItem, type ResultGallery } from '../studio/engine-kit/result-gallery';
import { handoffPhoto } from '../studio/engine-kit/result-handoff';
import { chatClick, chatFoot, doneP, openSettings } from '../studio/engine-kit/tail-done';
import {
  collageDefaults,
  collageFields,
  collageSupported,
  collageTray,
  inputsOf,
  photoIds,
  photoPolicyKey,
  publishedIds,
  viewOf,
  viewOfInputs,
  type CollageView,
} from './collage-inputs';

/* ---------- контракт с движком (features/collage/engine.ts, serve({...})) ---------- */

/** Фото коллажа в виде шаблона: id — номер файла, как SEQ шаблона (свои) или «dN» (примеры); key — запись моста загрузок (свои); pos — точка кадра по высоте, %; tone — средний цвет; f — фильтр */
export interface CollageFile {
  id: string | number;
  key?: string;
  url: string;
  /** Адрес записи моста (оригинал); url — его уменьшенная копия, когда она готова */
  src?: string;
  name: string;
  pos: number;
  tone: string | null;
  f: string;
}
/** Раскладка шаблона (LAYS) */
export interface CollageLayout {
  id: string;
  n: string;
  d: string;
  min: number;
  max: number;
  ex: number;
  tx: string;
  th: string[];
  pop: number;
  isNew: boolean;
  b: string;
}
/** Состояние экрана шаблона (S), см. engine.ts §2 */
export interface CollageState {
  lay: string;
  ar: string;
  gap: string;
  bg: string;
  files: CollageFile[];
  text: string;
  open: boolean;
  cheap: boolean;
  sh: { tab: string; theme: string | null; q: string } | null;
}
/** Снимок входа заказа (r.snap шаблона, runSnap): раскладка, ячеек, фото заказа, сколько фото всего (для «Ещё раскладка»), выборы, подпись */
export interface CollageSnap {
  lay: string;
  n: string;
  k: number;
  files: CollageFile[];
  all: number;
  ar: string;
  gap: string;
  bg: string;
  text: string;
  cheap: boolean;
}
/** Настройки страницы (BotCore.uiDefaults и init каркаса): то, что читает SRV */
export interface EngineUi {
  buzz(kind?: string): void;
  tip(el: Element | null, text: string): void;
  [key: string]: unknown;
}
/** Рамка загрузки BotCore.upload экрана (UP) */
interface EngineUpload {
  o: { mb?: number; multi?: number; [key: string]: unknown };
  say(text: string): void;
  paint(): boolean;
}
/** Подвал BotCore.foot экрана (FOOT) */
interface EngineFoot {
  poor(): boolean;
  poorOpen(): void;
  sync(): void;
}
/** Карточка «Ещё настройки» (BotCore.settingsCard) */
interface EngineSettings {
  sync(o?: unknown): void;
  close(): void;
  readonly isOpen?: boolean;
}
/** Крючки, которые движок отдаёт serve (engine.ts, после OWN и KEEP): tail, up, foot, set и rail — геттеры */
export interface CollageEngineHooks {
  S: CollageState;
  CAM: { st: 'idle' | 'asking' | 'on'; req: unknown; stream: unknown; sim: boolean };
  /** Раскладки, форматы, отступы и фоны шаблона: сужаются на месте до опубликованных */
  LAYS: CollageLayout[];
  FMTS: string[][];
  GAPS: Array<[string, string, number, number]>;
  BGS: string[][];
  /** Чипы подписи шаблона: [подпись, текст или функция (дата)] */
  CHIPS: Array<[string, string | (() => string)]>;
  /** Темы листа «Все раскладки» (на языке профиля — на месте) */
  THEMES: string[];
  readonly tail: BotRunApi;
  readonly up: EngineUpload;
  readonly foot: EngineFoot;
  readonly set: EngineSettings;
  readonly rail: { paint(): void; sync(o?: unknown): void; close(): void };
  root(): HTMLElement | null;
  mounted(): boolean;
  ui(): EngineUi;
  ic(name: string): string;
  esc(value: string): string;
  lay(): CollageLayout;
  layOf(id: string): CollageLayout;
  kOf(L: CollageLayout): number;
  slidesOf(k: number): number;
  canvasAr(L: CollageLayout, k: number, ar: string): [number, number];
  snapAr(snap: CollageSnap): [number, number];
  collageHTML(o: Record<string, unknown>): string;
  snapHTML(snap: CollageSnap): string;
  today(): string;
  toneOf(file: CollageFile): void;
  runWhat(snap: CollageSnap): string;
  setup(o: unknown): void;
  setLimits(o: { min?: number; max?: number; mb?: number; tmax?: number }): void;
  limits(): { min: number; max: number; tmax: number };
  setFiles(list: CollageFile[]): void;
  paintPage(): void;
  filesChanged(): void;
  pvPaint(anim?: boolean): void;
  syncFoot(): void;
  peeking(): boolean;
  peekClose(now?: boolean): void;
  sheetClose(): void;
  liveStop(): void;
  camStop(paint?: boolean): void;
  runSteps(): string[][];
}
type Missing = '' | 'load' | 'off' | 'blocked' | 'unavailable';
type Ready = '' | 'upload';

export interface CollageServer {
  ui<U extends object>(base: U): ServerUi<U>;
  /** Текстовый шов ядра (L-TX): строки BotCore словарём профиля */
  tx(template: string, ...values: unknown[]): string;
  entry(o: unknown): Record<string, unknown>;
  open(o: unknown): void;
  close(): void;
  tail(create: (o: Record<string, unknown>) => BotRunApi, opts: Record<string, unknown>): BotRunApi;
  wrap<T>(slot: 'cheap', value: T): T;
  missing(): Missing | null;
  ready(): Ready;
  missText(kind: string): string;
  label(): string | null;
  price(cheap: boolean): number;
  unit(): string;
  commit(): void;
  /** Примеры без своих фото: документ экрана (пусто — без примеров) */
  examples(): CollageFile[];
  /** Чипы подписи на языке профиля: документ экрана или чипы шаблона */
  chips(): Array<{ l: string; t: string }>;
  upOpts(): { accept: string; tx(template: string, ...values: unknown[]): string; title(): string; sub(): string; trayTitle(): string };
  pick(files: File[]): void;
  remove(i: number): void;
  camText(): string;
  start(btn: HTMLElement): void;
  snap(): CollageSnap;
  /** Картинка итога в рамке BotCore.rv.grid: PNG сервера (панорама — слайды в ряд), пока не пришёл — коллаж снимка разметкой */
  resPic(r: BotRunRun): string;
  /** Файл итога для «Скачать» (кнопка и лайтбокс): коллаж PNG, панорама — архив слайдов */
  file(r: BotRunRun): Promise<{ data: Blob; name: string }>;
  click(d: DOMStringMap, el: HTMLElement, r: BotRunRun | null): boolean;
  leave(go: () => void): void;
  steps(): string[][];
  drive(r: BotRunRun): void;
  failText(r: BotRunRun): string | null;
  receipt(r: BotRunRun): string;
  /** «Назад» внутри экрана: лист согласия, крупный вид раскладки, лист «Все раскладки» и карточка настроек закрываются первыми; true — шаг сделан */
  back(): boolean;
}

/* ---------- общие части ---------- */

interface Core {
  html: { unit(n: number, rest?: string): string };
}
const core = () => window.BotCore as unknown as Core;
const MB = 1024 * 1024;
/** «Занята» кнопка шаблона (SteosUI.busy): крутилка, пока идёт действие; уже занята — действие не начинается */
function busyButton<T>(btn: HTMLElement | null, fn: () => Promise<T>): Promise<T | undefined> {
  const busy = window.SteosUI?.busy;
  return btn && busy ? busy(btn, fn) : fn();
}
/** accept рамки BotCore (её проверка типа — до моста): любые фото и расширения, у которых браузер не называет тип;
    точные типы и предел — у моста по политике слота формы */
const ACCEPT = 'image/*,.jpg,.jpeg,.png,.webp,.heic,.heif';
const TRANSPORT = uploadFailureText(null);
const FORM_SAY = [
  'Некоторые файлы повтора больше недоступны. Загрузите их заново.',
  'Этот инструмент сейчас недоступен. Попробуйте обновить форму.',
  'Инструмент обновляется. Обновите форму позже.',
];
/** Тексты страницы заказа (T шаблона, engine.ts TAIL): failText берёт заголовки отказа и сбоя отсюда; отказ проверки — по снимку:
    с подписью — подпись, без неё — фото (как подпись кнопки родного хвоста failLabel) */
const FAIL = { techH: 'Не получилось собрать коллаж', notMade: 'коллаж не создан' };
const MOD = {
  cap: { h: 'Подпись не прошла проверку', why: 'Политику и запрещённые темы не пропускаем.', fix: 'Изменить подпись' },
  pic: { h: 'Фото не прошли проверку', why: 'На фото то, что мы не публикуем.', fix: 'Заменить фото' },
};
const modOf = (r: BotRunRun | null | undefined) => (String((r?.snap as Partial<CollageSnap> | undefined)?.text ?? '').trim() ? MOD.cap : MOD.pic);
/** Картинка итога во всю рамку коллажа (.rv-v) */
const IMG_STYLE = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block';
const RIGHTS = { row: 'Права на свои фото', sub: 'Подтверждаю право использовать загружаемые фото' };
/** Документ экрана (GET /app/studio/screens/collage): то, что читает экран */
interface CollageDoc {
  examples?: Array<{ url?: unknown; pos?: unknown; tone?: unknown; filter?: unknown }>;
  caption_chips?: Array<{ label?: unknown; text?: unknown; kind?: unknown }>;
}
/** Примеров на форме: ячеек у самой большой раскладки шаблона (9) */
const EXAMPLES = 9;
const str = (value: unknown) => (typeof value === 'string' ? value : '');
const num = (value: unknown, fallback: number) => (typeof value === 'number' && Number.isFinite(value) ? value : fallback);
/** Цвет примера (#rrggbb) — в стиль холста: только шестнадцатеричный */
const hex = (value: unknown) => (typeof value === 'string' && /^#[\da-f]{6}$/i.test(value) ? value : null);
/** Фильтр примера (CSS filter): только функции фильтра с числами */
const filterOf = (value: unknown) => (typeof value === 'string' && /^(\s*[a-z-]+\(\s*[\d.]+(%|deg)?\s*\))*\s*$/i.test(value) ? value.trim() : '');

/** Состояние одной привязки (кадра ServerStudio) */
interface Frame {
  b: EngineBinding;
  key: string;
  svc: OrderService;
  app: StudioApp;
  bridge: MediaBridge;
  mirror: InputsMirror;
  /** Лоток своих фото (слоты формы или JSON-список) — после первой сверки формы */
  tray: MediaTray | null;
  /** Форма, для которой прошла первая сверка (пределы, раскладки, форматы, отступы, фоны) */
  form: StudioForm | null;
  /** Раскладка формы на экране: опубликованные раскладки, форматы, отступы и фоны */
  layout: string;
  /** Набор id загрузок повтора, превью которого уже читали (не читать тот же набор по кругу) */
  restored: string;
  /** Ошибка формы, уже сказанная в рамке */
  said: string;
  consent: ConsentSheet | null;
  /** Форма была на экране при прошлой сверке: новый показ формы спрашивает остаток (как монтирование родной кнопки) */
  formShown: boolean;
  balanceAsk: boolean;
  orderSeen: number | undefined;
  /** Первая сверка формы (или ошибка формы) и документ экрана: готовность первой отрисовки (EngineBound.ready) */
  settled: () => void;
  first: Promise<void>;
  driver: RunDriver;
  /** Подсмотренная при открытии заявка (до проверки владельца сервисом) */
  peek: Intent | null;
  /** S сброшен для этого кадра: входы сервиса переносятся в S при ближайшей сверке */
  fresh: boolean;
  /** Фото шаблона по записям моста (один объект на запись: средний цвет и ключ предпросмотра держатся) */
  files: Map<string, CollageFile>;
  /** Уменьшенные копии фото по адресу записи моста: null — копия готовится или не нужна (фото и так небольшое) */
  thumbs: Map<string, string | null>;
  /** Номера своих фото — как SEQ шаблона (fileOf: id:++SEQ): ключ предпросмотра pvKey (data-k #clPc) тот же, что у шаблона */
  seq: number;
  /** Картинки итога (один набор на id:revision показанного итога) */
  gallery: ResultGallery;
  /** Набор итога, по которому страница заказа уже перерисована */
  shownKey: string;
  /** «Рилс из этих фото» в пути: второе нажатие не передаёт ещё раз */
  moving: boolean;
  /** Документ экрана (примеры, чипы подписи): ответ — владельца, для которого его спросили; открытие рисуется из памяти раздела,
      запрос кадра подтверждает её (screen-doc.ts) */
  doc: ScreenDoc<CollageDoc>;
}

/* ---------- сервер экрана ---------- */

let instance: { bind(b: EngineBinding): EngineBound } | null = null;

export function createCollageServer(h: CollageEngineHooks): CollageServer {
  const { S } = h;
  let frame: Frame | null = null;
  let bound: Frame | null = null;
  /** Кадр, к которому относятся S и экземпляр хвоста BotRun (объект, а не ключ: ключи кадров повторяются после нового входа в раздел) */
  let painted: Frame | null = null;
  let tailOpts: { create: (o: Record<string, unknown>) => BotRunApi; opts: Record<string, unknown> } | null = null;
  let inst: BotRunApi | null = null;
  const facade = {} as BotRunApi;
  /** Шаблонные значения S на новый кадр (engine.ts §2) */
  const fresh = { lay: S.lay, ar: S.ar, gap: S.gap, bg: S.bg };
  /** Полные списки шаблона: сужение на месте всегда идёт от них (другая форма может публиковать больше) */
  const ALL = { lays: h.LAYS.slice(), fmts: h.FMTS.slice(), gaps: h.GAPS.slice(), bgs: h.BGS.slice(), themes: h.THEMES.slice() };
  /** Подписи списков шаблона на языке профиля: копии (полные списки ALL остаются строками шаблона) */
  const said = {
    lay: (item: CollageLayout): CollageLayout => ({ ...item, n: uiText(item.n), d: uiText(item.d), th: themesOf(item.th).map(theme => uiText(theme)), b: uiText(item.b) }),
    fmt: ([value, word, sub]: string[]) => (sub === undefined ? [value, uiText(word)] : [value, uiText(word), sub]),
    gap: ([value, word, g, r]: [string, string, number, number]): [string, string, number, number] => [value, uiText(word), g, r],
    bg: ([value, word]: string[]) => [value, uiText(word)],
  };
  /** Темы раскладки — темы листа: шаблон режет строку тем по пробелу («С текстом» → «С», «текстом»), поэтому вкладки «С текстом» и
      «Для соцсетей» у него пусты; слова, которые вместе дают тему листа, снова одна тема (её переводит словарь) */
  function themesOf(words: string[]): string[] {
    const out: string[] = [];
    for (let i = 0; i < words.length; i++) {
      const joined = ALL.themes.find(theme => {
        const parts = theme.split(' ');
        return parts.length > 1 && parts.every((part, j) => words[i + j] === part);
      });
      if (joined) {
        out.push(joined);
        i += joined.split(' ').length - 1;
      } else out.push(words[i]);
    }
    return out;
  }
  /** Форма последней сверки (списки на новом языке сужаются по ней) */
  let listForm: StudioForm | null = null;
  let examplesOf: { data: CollageDoc | undefined; list: CollageFile[] } | null = null;

  const cur = () => (bound && bound === frame ? frame : null);
  const live = (f: Frame) => {
    const s = f.svc.get();
    return { s, d: f.svc.derive(s) };
  };
  const onForm = () => h.mounted() && !h.tail.run;
  /** Подсказка-стекло у элемента; элемента нет на месте — у кнопки подвала */
  const tip = (el: Element | null, text: string) => {
    const at = el?.isConnected ? el : (h.root()?.querySelector('.ap-foot .btn') ?? null);
    if (text && at) h.ui().tip(at, uiText(text));
  };
  /** Заявка заказа: сервиса, а пока он не проверил владельца — подсмотренная при открытии */
  const intentOf = (f: Frame | null): Intent | null => {
    const s = f?.svc.get();
    if (!f || !s) return null;
    return s.intent ?? (!s.identity && !s.integrityError ? f.peek : null);
  };
  /** Вид S для входов */
  const sView = (): CollageView => ({ lay: S.lay, ar: S.ar, gap: S.gap, bg: S.bg, text: S.text });

  /* ---- свои фото лотка ---- */
  /** Длинная сторона копии для экрана, px: предпросмотр и 14 карточек ленты рисуют каждое фото полтора десятка раз */
  const THUMB_PX = 1024;
  /** Копия фото для экрана. Снимки телефона в полном размере (9 фото × ~15 мест) не умещались в память WebView:
      часть кружков оставалась серой (04.11.2026, «догруженные фото не прогружаются»). Заказ уходит с оригиналом — загрузкой моста */
  function thumbOf(f: Frame, src: string) {
    if (f.thumbs.has(src)) return;
    f.thumbs.set(src, null);
    const image = new Image();
    image.onload = () => {
      const scale = THUMB_PX / Math.max(image.naturalWidth, image.naturalHeight);
      if (!(scale < 1) || !f.thumbs.has(src)) return;
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(image.naturalWidth * scale);
      canvas.height = Math.round(image.naturalHeight * scale);
      const g = canvas.getContext('2d');
      if (!g) return;
      g.drawImage(image, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        blob => {
          if (!blob || !f.thumbs.has(src)) return;
          f.thumbs.set(src, URL.createObjectURL(blob));
          trayChanged(f);
        },
        'image/jpeg',
        0.86,
      );
    };
    image.src = src;
  }
  /** Фото шаблона записи моста (тот же объект, пока адрес тот же: средний цвет считается один раз; копия пришла — новый объект) */
  function fileOf(f: Frame, item: MediaItem): CollageFile {
    let file = f.files.get(item.id);
    thumbOf(f, item.url);
    const url = f.thumbs.get(item.url) ?? item.url;
    if (!file || file.src !== item.url || file.url !== url) {
      file = { id: ++f.seq, key: item.id, url, src: item.url, name: item.name, pos: file?.src === item.url ? file.pos : 40, tone: null, f: '' };
      f.files.set(item.id, file);
      h.toneOf(file);
    }
    return file;
  }
  /** S.files — записи лотка моста, у которых есть что показать (превью повтора, пока читается, не показывается) */
  function filesOf(f: Frame): CollageFile[] {
    const list = (f.tray?.list() ?? []).filter(item => !!item.url);
    const ids = new Set(list.map(item => item.id));
    for (const id of f.files.keys()) if (!ids.has(id)) f.files.delete(id);
    /* копии убранных фото отпускаются; пока идёт заказ — нет: хвост показывает снимок формы */
    if (!h.tail.run) {
      const srcs = new Set(list.map(item => item.url));
      for (const [src, thumb] of f.thumbs)
        if (!srcs.has(src)) {
          f.thumbs.delete(src);
          if (thumb) URL.revokeObjectURL(thumb);
        }
    }
    return list.map(item => fileOf(f, item));
  }
  /** Лоток моста изменился: S.files заново, рамка и всё, что зависит от фото (предпросмотр, лента, лист, кнопка) */
  function trayChanged(f: Frame) {
    if (cur() !== f) return;
    h.setFiles(filesOf(f));
    if (!onForm()) return;
    h.filesChanged();
  }
  /** Фото одного выбора приходят в лоток по одному (put моста на каждый файл): рамка перерисовывается один раз на выбор, как у шаблона,
      где выбранные фото встают разом. Иначе trayPaint шаблона видит каждое следующее фото «новым» и докручивает ленту в конец */
  const putting = new WeakSet<Frame>();
  function putSoon(f: Frame) {
    if (putting.has(f)) return;
    putting.add(f);
    queueMicrotask(() => {
      putting.delete(f);
      trayChanged(f);
    });
  }
  /** Права на фото не подтверждены, а фото повтора стоят в лотке: кнопка подтверждает их (лист согласия без файла) */
  const rightsNeeded = (f: Frame) => !f.svc.get().rights && !!f.tray?.list().some(item => item.state === 'ready');
  function confirmRights(f: Frame) {
    f.consent?.close();
    f.consent = openConsentSheet({
      rights: () => f.svc.get().rights,
      locked: () => f.svc.derive().locked,
      setRights: on => f.svc.setters.rights(on),
      confirm: true,
      texts: RIGHTS,
      onContinue: () => {
        f.consent = null;
        if (cur() === f) h.foot.sync();
      },
      onClose: () => {
        f.consent = null;
      },
    });
  }

  /* ---- готовность ---- */
  function missing(): Missing | null {
    const f = cur();
    if (!f) return 'load';
    const { s, d } = live(f);
    if (s.intent && !d.orderId) return '';
    if (!s.identity && !s.integrityError) return 'load';
    if (s.integrityError || !s.identity || s.identity.appId !== f.app.id) return 'blocked';
    /* форма не загрузилась или устарела: кнопка «Обновить форму» (recovery) */
    if (!s.form) return s.error ? '' : 'load';
    if (s.versionStale) return '';
    if (!collageSupported(s.form)) return 'off';
    if (!s.form.available) return 'unavailable';
    /* чинить, а не создавать: «Продолжить загрузку», «Подтвердите права на фото» — кнопка активна */
    if (recovery(f)) return '';
    return null;
  }
  function ready(): Ready {
    const f = cur();
    if (!f) return 'upload';
    /* фото ещё грузятся или читаются превью повтора */
    return f.bridge.pending() ? 'upload' : '';
  }
  /** Что починить вместо создания: форма не загрузилась или устарела — перечитать; загрузка не подтверждена — продолжить тем же id;
      фото повтора без подтверждённых прав — подтвердить */
  function recovery(f: Frame): ((btn: HTMLElement) => void) | null {
    const { s } = live(f);
    if ((!s.form && s.error && !s.integrityError) || s.versionStale) return () => f.svc.setters.revision(value => value + 1);
    if (f.bridge.stalled() && !s.busy && s.error === TRANSPORT) return btn => void busyButton(btn, () => f.bridge.resume());
    if (s.form && rightsNeeded(f)) return () => confirmRights(f);
    return null;
  }
  function label(): string | null {
    const f = cur();
    if (!f) return null;
    const { s, d } = live(f);
    if (s.intent && !d.orderId) return uiText('Продолжить проверку');
    if ((!s.form && s.error && !s.integrityError) || s.versionStale) return uiText('Обновить форму');
    if (f.bridge.stalled() && !s.busy && s.error === TRANSPORT) return uiText('Продолжить загрузку');
    if (s.form && rightsNeeded(f)) return uiText('Подтвердите права на фото');
    if (s.form?.chargeable === false) return uiText('Создать коллаж');
    return null;
  }
  /** Скидка «дешевле» формы — только для той цены, для которой она посчитана (цена формы, D11) */
  function cheapOffer(f: Frame | null) {
    if (!f) return null;
    const { s, d } = live(f);
    const offer = studioCheapOffer(s.form);
    return offer && s.form && d.price === s.form.expected_price ? offer : null;
  }

  /* ---- сверка формы с сервисом (каждый зафиксированный вид) ---- */
  /** Раскладки, форматы, отступы и фоны шаблона — только опубликованные (порядок шаблона); поля нет — как в шаблоне */
  function narrow(form: StudioForm | null) {
    listForm = form;
    const f = collageFields(form);
    const cut = <T>(list: T[], all: T[], keep: (item: T) => boolean) => {
      const next = all.filter(keep);
      list.splice(0, list.length, ...(next.length ? next : all));
    };
    const ids = {
      lay: publishedIds(f.layout, 'layout'),
      ar: publishedIds(f.ratio, 'ratio'),
      gap: publishedIds(f.gap, 'gap'),
      bg: publishedIds(f.bg, 'bg'),
    };
    /* копии на языке профиля (id те же) */
    cut(h.LAYS, ALL.lays.map(said.lay), item => !ids.lay || ids.lay.includes(item.id));
    cut(h.FMTS, ALL.fmts.map(said.fmt), item => !ids.ar || ids.ar.includes(item[0]));
    cut(h.GAPS, ALL.gaps.map(said.gap), item => !ids.gap || ids.gap.includes(item[0]));
    cut(h.BGS, ALL.bgs.map(said.bg), item => !ids.bg || ids.bg.includes(item[0]));
    h.THEMES.splice(0, h.THEMES.length, ...ALL.themes.map(theme => uiText(theme)));
    /* выбор вне опубликованного — первый опубликованный */
    if (!h.LAYS.some(item => item.id === S.lay)) S.lay = h.LAYS[0].id;
    if (!h.FMTS.some(item => item[0] === S.ar)) S.ar = h.FMTS[0][0];
    if (!h.GAPS.some(item => item[0] === S.gap)) S.gap = h.GAPS[0][0];
    if (!h.BGS.some(item => item[0] === S.bg)) S.bg = h.BGS[0][0];
  }
  /** Входы → S (повтор, «Изменить», заявка родной формы, значения по умолчанию) */
  function applyView(view: CollageView) {
    S.text = view.text;
    if (view.lay && h.LAYS.some(item => item.id === view.lay)) S.lay = view.lay;
    if (view.ar && h.FMTS.some(item => item[0] === view.ar)) S.ar = view.ar;
    if (view.gap && h.GAPS.some(item => item[0] === view.gap)) S.gap = view.gap;
    if (view.bg && h.BGS.some(item => item[0] === view.bg)) S.bg = view.bg;
  }
  /** Фото повтора: id во входах, которых нет в лотке, — превью (один раз на набор); записи лотка, которых больше нет во входах
      (сервис снял загрузку), уходят */
  function photosSync(f: Frame) {
    const tray = f.tray;
    if (!tray) return;
    const { s, d } = live(f);
    const ids = photoIds(s.form, s.inputs);
    const list = tray.list();
    const known = (id: string) => list.some(item => item.uploadId === id || item.pendingId === id);
    if (ids.length && !ids.every(known) && f.restored !== ids.join()) {
      f.restored = ids.join();
      void tray.restore(ids).finally(() => {
        if (cur() === f && h.mounted()) changed(f);
      });
      return;
    }
    if (d.locked || f.bridge.pending()) return;
    for (const item of list) if (item.state === 'ready' && item.uploadId && !ids.includes(item.uploadId)) tray.remove(item.id);
  }
  /** Раскладка формы на экране: опубликованные раскладки, форматы, отступы и фоны (без формы — пусто) */
  const layoutOf = (f: Frame) =>
    f.svc.get().form
      ? JSON.stringify([accountLocale(), h.LAYS.map(item => item.id), h.FMTS.map(item => item[0]), h.GAPS.map(item => item[0]), h.BGS.map(item => item[0]), h.limits()])
      : '';
  /** Сверка без отрисовки: первая форма (пределы, списки, лоток), значения по умолчанию, входы → S.
      → входы изменили S (форму надо нарисовать заново) */
  function prime(f: Frame): boolean {
    const { s, d } = live(f);
    const form = s.form;
    if ((form || s.error || s.integrityError) && f.settled) f.settled();
    if (form && f.form !== form) {
      /* первая сверка этой формы: пределы лотка и подписи, опубликованные списки */
      f.form = form;
      const spec = collageTray(form);
      const key = photoPolicyKey(form);
      const policy = key ? mediaPolicy(form, key) : null;
      f.tray = spec ? f.bridge.slots(spec) : null;
      const max = f.tray ? f.tray.max() : 0;
      h.setLimits({
        min: Math.max(2, form.constraints?.min_photos ?? 2),
        max,
        mb: policy ? policy.maxBytes / MB : 0,
        tmax: collageFields(form).caption?.max_length ?? 0,
      });
      narrow(form);
    }
    /* значения по умолчанию — только в опубликованные выборы и не поверх заявки (как родная форма) */
    if (form && !d.locked && !s.intent) {
      const add = collageDefaults(form, s.inputs);
      if (add.length)
        f.svc.setters.inputs(previous => {
          const next = { ...previous };
          for (const [key, value] of add) if (!next[key]) next[key] = value;
          return next;
        });
    }
    let repaint = false;
    const now = f.svc.get();
    if (form && (f.fresh || f.mirror.external(now.inputs))) {
      /* S только что сброшен (другой кадр рисовал экран) или входы изменило не зеркало: в S — если они не те, что S дал бы сам
         (запись моста о фото S не трогает) */
      if (f.fresh || !sameInputs(inputsOf(form, sView(), now.inputs), now.inputs)) {
        applyView(viewOf(form, now.inputs));
        repaint = true;
      }
      f.fresh = false;
      f.mirror.seen(now.inputs);
    }
    return repaint;
  }
  /** Форма на экран: сверка без отрисовки, затем одна отрисовка (её раскладка запоминается) */
  function paintForm(f: Frame) {
    prime(f);
    f.layout = layoutOf(f);
    h.setFiles(filesOf(f));
    h.paintPage();
  }
  function formSync(f: Frame) {
    const repaint = prime(f);
    const now = f.svc.get();
    if (now.form && onForm()) photosSync(f);
    const layout = layoutOf(f);
    if (layout !== f.layout || repaint) {
      f.layout = layout;
      if (onForm()) h.paintPage();
    }
    if (!onForm()) return;
    /* ошибка формы (не загрузилась, файлы повтора недоступны) — один раз в рамке */
    const error = now.integrityError ? '' : now.error;
    if (error !== f.said) {
      f.said = error;
      if (FORM_SAY.includes(error)) h.up.say(uiText(error));
    }
    h.foot.sync();
  }
  /** S.files — те же записи, что в лотке (мост убрал или переставил запись без своего отклика) */
  function photosCheck(f: Frame) {
    const now = (f.tray?.list() ?? []).filter(item => !!item.url);
    if (now.length !== S.files.length || now.some((item, i) => item.id !== S.files[i]?.key || item.url !== S.files[i]?.src)) trayChanged(f);
  }
  function changed(f: Frame) {
    if (cur() !== f || !h.mounted()) return;
    photosCheck(f);
    formSync(f);
    f.bridge.sync();
    f.driver.sync();
    f.consent?.refresh();
    balance(f);
  }
  /** Остаток для «Не хватает Circle» — тот же, что у родной кнопки (GET /app/balance, память на минуту): спрашивается при каждом
      показе формы, если в памяти его нет; созданный или открытый заказ делает память устаревшей */
  function balance(f: Frame) {
    const orderId = f.svc.derive().orderId;
    if (orderId && orderId !== f.orderSeen) {
      f.orderSeen = orderId;
      forgetCircleBalance();
    }
    const shown = onForm(),
      first = shown && !f.formShown;
    f.formShown = shown;
    if (!first || f.balanceAsk || circleBalance() !== null || f.svc.get().form?.chargeable === false) return;
    f.balanceAsk = true;
    const seen = f.orderSeen;
    void endpoints
      .balance()
      .then(
        value => {
          if (f.orderSeen === seen) rememberCircleBalance(value?.circle);
        },
        () => {
          /* ошибка — проверки нет, решает сервер (402), как у родной кнопки */
        },
      )
      .finally(() => {
        f.balanceAsk = false;
        if (cur() === f && onForm()) h.foot.sync();
      });
  }
  /** Документ экрана пришёл (или сменился владелец): примеры и чипы — заново; на форме предпросмотр и лента — по ним */
  function docChanged(f: Frame) {
    examplesOf = null;
    if (cur() !== f || !onForm()) return;
    h.pvPaint();
    h.rail.paint();
  }

  /* ---- хвост BotRun: фасад одного экземпляра на кадр ---- */
  function rebuild() {
    if (!tailOpts) return;
    const { create, opts } = tailOpts;
    const base = typeof opts.missing === 'function' ? (opts.missing as () => string) : () => '';
    /* тексты страницы заказа шаблона (T) — словарём профиля в момент показа (BotRun берёт их через val); отказ проверки — по снимку,
       подпись итога — по доставке и числу слайдов панорамы */
    const given = (opts.T as Record<string, unknown>) ?? {};
    const T = Object.fromEntries(Object.entries(given).map(([key, value]) => [key, typeof value === 'string' ? () => uiText(value) : value]));
    const head = (opts.head as Record<string, unknown>) ?? {};
    const title = head.title;
    /* «Изменить подпись» / «Заменить фото» — по снимку заказа (BotRun вставляет подпись подвала разметкой) */
    const foot = { ...((opts.foot as Record<string, unknown>) ?? {}), mod: ['data-cl-edit', 'edit', (r: BotRunRun) => esc(uiText(modOf(r).fix))] };
    /* действия итога и «Что дальше» — подписи словарём (BotRun вставляет их разметкой) */
    const labelled = (list: unknown) =>
      Array.isArray(list) ? list.map(item => (Array.isArray(item) && typeof item[2] === 'string' ? [item[0], item[1], esc(uiText(item[2]))] : item)) : list;
    const acts = opts.acts as ((r: BotRunRun) => unknown) | undefined,
      next = opts.next as ((r: BotRunRun) => unknown) | undefined;
    inst = create({
      ...opts,
      T: {
        ...T,
        modH: (r: BotRunRun) => uiText(modOf(r).h),
        modWhy: (r: BotRunRun) => uiText(modOf(r).why),
        doneP: (r: BotRunRun) => doneText(r),
      },
      head: { ...head, title: (r: BotRunRun) => uiText(String(typeof title === 'function' ? (title as (r: BotRunRun) => unknown)(r) : (title ?? ''))) },
      ...(acts ? { acts: (r: BotRunRun) => labelled(acts(r)) } : {}),
      ...(next ? { next: (r: BotRunRun) => labelled(next(r)) } : {}),
      card: (r: BotRunRun) => card(r),
      drive: (r: BotRunRun) => server.drive(r),
      failText: (r: BotRunRun) => server.failText(r),
      receipt: (r: BotRunRun) => server.receipt(r),
      foot: chatFoot(serverFoot(foot, 'cl'), 'cl'),
      steps: () => server.steps(),
      snap: () => server.snap(),
      price: () => cur()?.svc.derive().price ?? 0,
      /* повтор заявки и «Попробовать ещё раз» решает сервис: форма в памяти им не нужна */
      missing: () => {
        const f = cur();
        if (f) {
          const { s, d } = live(f);
          if (s.intent || d.orderId) return '';
        }
        return base();
      },
    });
    for (const key of Object.keys(inst)) {
      if (key === 'run' || Object.hasOwn(facade, key)) continue;
      (facade as unknown as Record<string, unknown>)[key] = (...args: unknown[]) =>
        (inst as unknown as Record<string, (...a: unknown[]) => unknown>)[key](...args);
    }
  }
  Object.defineProperty(facade, 'run', { enumerable: true, get: () => inst?.run ?? null });

  /* ---- привязка кадра ---- */
  function bind(b: EngineBinding): EngineBound {
    if (!frame || frame.key !== b.key || frame.svc !== b.svc) {
      if (frame) {
        frame.bridge.cancel();
        frame.consent?.close();
        frame.doc.abort();
        frame.gallery.release();
      }
      frame = makeFrame(b);
    } else frame.b = b;
    const mine = frame;
    bound = mine;
    return {
      changed: () => changed(mine),
      release() {
        if (bound === mine) bound = null;
      },
      ready: () => mine.first,
      back: () => cur() === mine && server.back(),
    };
  }
  function makeFrame(b: EngineBinding): Frame {
    const f = {} as Frame;
    const guard =
      <A extends unknown[]>(fn: (...args: A) => void) =>
      (...args: A) => {
        if (frame === f) fn(...args);
      };
    let settle!: () => void;
    const formFirst = new Promise<void>(resolve => (settle = resolve));
    Object.assign(f, {
      b,
      key: b.key,
      svc: b.svc,
      app: b.app,
      tray: null,
      form: null,
      layout: '',
      restored: '',
      said: '',
      consent: null,
      formShown: false,
      balanceAsk: false,
      orderSeen: undefined,
      /* первая отрисовка — после формы и документа экрана (примеры и чипы не встают под уже показанной формой) */
      first: formFirst,
      settled: () => {
        settle();
        f.settled = undefined as unknown as () => void;
      },
    });
    f.mirror = createInputsMirror(b.svc);
    f.peek = null;
    f.fresh = true;
    f.files = new Map();
    f.thumbs = new Map();
    f.seq = 0;
    f.gallery = createResultGallery(ORDER_IMAGE_LIMITS);
    f.shownKey = '';
    f.moving = false;
    f.doc = screenDoc<CollageDoc>('collage', { remember: true, onChange: () => docChanged(f) });
    f.first = Promise.all([f.first, f.doc.load()]).then(() => undefined);
    f.driver = createRunDriver({
      svc: b.svc,
      tail: () => h.tail,
      root: () => h.root(),
      mounted: () => cur() === f && h.mounted(),
      localSteps: () => server.steps(),
      paintForm: () => (cur() === f ? paintForm(f) : h.paintPage()),
      tip: (el, text) => tip(el, text),
      onDone: () => onDone(f),
    });
    f.bridge = createMediaBridge({
      svc: b.svc,
      owner: () => f.svc.get().identity?.owner,
      say: guard((text: string) => h.up.say(text)),
      put: guard(() => putSoon(f)),
      drop: guard(() => trayChanged(f)),
      update: guard(() => trayChanged(f)),
      consentWith: () => ({ texts: RIGHTS }),
    });
    return f;
  }

  const server: CollageServer = {
    ui: base =>
      serverUi(base, {
        poorBalance: () => {
          const f = cur();
          /* кнопка чинит («Обновить форму», «Продолжить загрузку»), а не создаёт: «Не хватает · Пополнить» её не заслоняет */
          if (!f || recovery(f)) return Infinity;
          const s = f.svc.get();
          return poorBalance({ balance: circleBalance(), form: s.form, intent: s.intent }, true);
        },
      }),
    tx: (template, ...values) => uiFormat(template, ...values),
    /* вход экрана — входы сервиса (повтор, «Изменить»): фото с другого экрана, раскладка и формат шаблона в setup не идут — S ставит сверка формы */
    entry: () => ({}),
    open(o) {
      const f = cur();
      if (!f) {
        /* без привязки (кадр не активен) — форма без действий: missing() держит «Проверяем доступность» */
        h.setup(o);
        h.paintPage();
        return;
      }
      if (painted !== f) {
        /* другой кадр или новый сервис под прежним ключом: фото, подпись, выборы, скидка и хвост прежнего сюда не переходят */
        h.liveStop();
        h.sheetClose();
        /* списки шаблона — полные, пока форма этого кадра их не сузит */
        narrow(null);
        Object.assign(S, { lay: fresh.lay, ar: fresh.ar, gap: fresh.gap, bg: fresh.bg, text: '', files: [], open: false, cheap: false, sh: null });
        rebuild();
        painted = f;
        f.layout = '';
        f.form = null;
        f.fresh = true;
      }
      /* открытие на форме — новый показ кнопки: остаток спрашивается, если его нет в памяти */
      f.formShown = false;
      void f.doc.load();
      h.setup(o);
      h.setFiles(filesOf(f));
      const root = h.root();
      if (h.tail.run && root) {
        h.tail.show(root);
        changed(f);
        return;
      }
      f.peek = peekIntent(f.app.id);
      /* форма рисуется один раз (paintForm водителя): сверка перерисует её, только если раскладка или входы станут другими */
      if (f.driver.first(f.peek) === 'form' && root) root.scrollTop = 0;
      changed(f);
    },
    close() {
      const f = frame;
      if (!f) return;
      f.consent?.close();
      f.consent = null;
    },
    tail(create, opts) {
      tailOpts = { create, opts };
      rebuild();
      return facade;
    },
    wrap<T>(_slot: 'cheap', value: T): T {
      const cheap = value as unknown as { where?: unknown; go?: unknown };
      /* скидку «Сделать дешевле» публикует форма, и она посчитана для текущей цены: без неё у подвала нет ни цены, ни действия скидки;
         цена со скидкой — цена формы (не BotCore.cheapPrice) */
      return {
        /* где водяной знак (лист «Не хватает Circle») — словарём профиля в момент показа */
        where: () => uiText(String(cheap.where ?? '')),
        get price() {
          return cheapOffer(cur()) ? () => server.price(true) : undefined;
        },
        get go() {
          return cheapOffer(cur()) ? cheap.go : undefined;
        },
        get percent() {
          return cheapOffer(cur())?.percent;
        },
      } as unknown as T;
    },
    missing,
    ready,
    missText(kind) {
      const f = cur();
      const s = f?.svc.get();
      switch (kind) {
        case 'load':
          return uiText('Проверяем доступность');
        case 'blocked':
          return uiText(s?.integrityError || 'Проверяем доступность');
        case 'off':
          return uiText('Этот вариант пока недоступен');
        case 'unavailable':
          return uiText('Создание сейчас недоступно');
        case 'upload':
          return uiText('Загружаем фото');
        default:
          return '';
      }
    },
    label,
    price(cheap) {
      const f = cur();
      if (!f) return 0;
      const offer = cheap ? cheapOffer(f) : null;
      return offer ? offer.price : f.svc.derive().price;
    },
    unit() {
      /* как у родной шапки: цена формы за коллаж (бесплатная форма — без строки цены); до формы — цена приложения из каталога */
      const f = cur() ?? frame;
      const form = f?.svc.get().form;
      const amount = form ? (form.chargeable === false ? null : form.expected_price) : toolPrice(f?.app.price).amount;
      return amount !== null && amount !== undefined && amount > 0 ? core().html.unit(amount, uiText('за коллаж')) : '';
    },
    commit() {
      const f = cur();
      const form = f?.svc.get().form;
      /* S ещё не принял входы этой формы (первая сверка впереди): писать нечего — иначе выборы шаблона легли бы поверх повтора */
      if (!f || !form || f.fresh || f.form !== form) return;
      /* порядок фото — порядок записей лотка: перестановка и «Перемешать» переписывают ключи слотов без новой загрузки */
      const tray = f.tray;
      if (tray && S.files.length) {
        const list = tray.list();
        const want = S.files.flatMap(file => (file.key ? [file.key] : [])).filter(id => list.some(item => item.id === id));
        const rest = list.filter(item => !want.includes(item.id)).map(item => item.id);
        const next = [...want, ...rest];
        if (next.join() !== list.map(item => item.id).join()) tray.order(next);
      }
      f.mirror.commit(previous => inputsOf(form, sView(), previous));
    },
    examples() {
      const data = (cur() ?? frame)?.doc.data();
      if (examplesOf && examplesOf.data === data) return examplesOf.list;
      const list: CollageFile[] = [];
      (Array.isArray(data?.examples) ? data.examples : []).forEach((item, i) => {
        const url = safeMediaUrl(item?.url);
        if (url) list.push({ id: `d${i}`, url, name: uiFormat('Пример {0}', i + 1), pos: num(item.pos, 40), tone: hex(item.tone), f: filterOf(item.filter) });
      });
      /* примеров меньше, чем ячеек у самой большой раскладки: по кругу (геометрия та же, пустых ячеек нет — как у родной формы) */
      for (let i = list.length; list.length && i < EXAMPLES; i++) list.push({ ...list[i % list.length], id: `d${i}` });
      examplesOf = { data, list };
      return list;
    },
    chips() {
      const given = (cur() ?? frame)?.doc.data()?.caption_chips;
      if (Array.isArray(given) && given.length)
        return given
          .map(chip => ({ l: uiText(str(chip?.label)), t: chip?.kind === 'date' ? h.today() : uiText(str(chip?.text)) }))
          .filter(chip => chip.l && chip.t);
      return h.CHIPS.map(([l, x]) => ({ l: uiText(l), t: typeof x === 'function' ? x() : uiText(x) }));
    },
    upOpts: () => ({
      accept: ACCEPT,
      /* свои строки рамки BotCore.upload («Загрузить», «Снять», «Можно до N фото», «N из M») — словарём профиля */
      tx: (template: string, ...values: unknown[]) => uiFormat(template, ...values),
      /* заголовок пустой рамки — по пределам формы */
      title: () => uiFormat('Загрузите от {0} до {1} фото', h.limits().min, h.limits().max),
      /* подпись пустой рамки и заголовок лотка шаблона — строки на момент создания рамки: словарём профиля в момент показа */
      sub: () => uiText('Порядок поменяете прямо в раскладке'),
      trayTitle: () => uiText('Фото'),
    }),
    pick(files) {
      const f = cur();
      if (!f || !files.length) return;
      const tray = f.tray;
      if (!tray) {
        h.up.say(uiText('Проверяем доступность'));
        return;
      }
      const room = tray.max() - tray.list().length;
      if (room <= 0) {
        h.up.say(uiFormat('Можно до {0} фото', tray.max()));
        return;
      }
      void tray.pickSlots(files.slice(0, room));
    },
    remove(i) {
      const f = cur();
      const id = S.files[i]?.key;
      if (!f?.tray || id === undefined) return;
      f.tray.remove(id);
      trayChanged(f);
    },
    camText: () => uiText('Камера недоступна. Загрузите файл.'),
    start(btn) {
      const f = cur();
      if (!f) return;
      const { s, d } = live(f);
      if (s.intent && !d.orderId) {
        h.tail.start();
        return;
      }
      const fix = recovery(f);
      if (fix) {
        fix(btn);
        return;
      }
      if (h.foot.poor()) {
        h.foot.poorOpen();
        return;
      }
      h.tail.start();
    },
    /* снимок входа заказа (r.snap шаблона): из тела заявки (подсмотренной), без неё — из S; фото — только те, что ушли в заказ
       (по порядку слотов), без них (после перезагрузки) — ячейки пустые */
    snap() {
      const f = cur();
      const s = f?.svc.get();
      const it = intentOf(f);
      const view = it ? viewOfInputs(s?.form, it.body.inputs as Record<string, unknown>) : sView();
      const L = h.layOf(view.lay ?? S.lay);
      const ids = it ? photoIds(s?.form, it.body.inputs as Record<string, unknown>) : null;
      const list = f?.tray?.list() ?? [];
      const files = ids
        ? ids.flatMap(id => {
            const item = list.find(entry => entry.uploadId === id && !!entry.url);
            return item && f ? [fileOf(f, item)] : [];
          })
        : S.files.slice();
      const n = ids ? ids.length : files.length;
      const k = n ? Math.max(L.min, Math.min(L.max, n)) : L.ex;
      return {
        lay: L.id,
        n: L.n,
        k,
        files: files.slice(0, k).map(file => ({ ...file })),
        all: n,
        /* значение заявки, которого форма больше не публикует, — выбор формы (подписи и геометрия шаблона берут только свои списки) */
        ar: view.ar && h.FMTS.some(item => item[0] === view.ar) ? view.ar : S.ar,
        gap: view.gap && h.GAPS.some(item => item[0] === view.gap) ? view.gap : S.gap,
        bg: view.bg && h.BGS.some(item => item[0] === view.bg) ? view.bg : S.bg,
        text: view.text.trim(),
        cheap: !!(it ? it.body.cheap : S.cheap),
      };
    },
    click(d, el, r) {
      const f = cur();
      if (d.clRetry !== undefined || d.clRchk !== undefined || d.clFix !== undefined) {
        if (!f || f.driver.busy()) return true;
        if (d.clRetry !== undefined) h.tail.retry();
        else if (d.clRchk !== undefined) void f.driver.check(el);
        else void f.driver.leave(() => h.tail.exit(() => h.paintPage()), false, '');
        return true;
      }
      if (d.clChat !== undefined) {
        chatClick({ order: f?.svc.get().order, el, text: 'Тут откроется тема чата, куда бот прислал коллаж', tip: (at, text) => tip(at, text) });
        return true;
      }
      if (d.clSettings !== undefined) {
        openSettings();
        return true;
      }
      /* «Рилс из этих фото» — картинка итога в «Рилс» после сверки владельца и заказа (Q-CS6, как родной хвост) */
      if (d.clReels !== undefined) {
        if (f && r?.st === 'done') handoff(f, el);
        return true;
      }
      return false;
    },
    resPic(r) {
      const f = cur() ?? frame;
      const items = f ? f.gallery.items(f.svc.get().order) : [];
      if (!items.length) return h.snapHTML(r.snap as unknown as CollageSnap);
      if (items.length === 1) return `<img src="${esc(items[0].url)}" alt="" draggable="false" style="${IMG_STYLE}">`;
      /* панорама: слайды сервера по порядку в один ряд рамки (стыки — там же, где разрез шаблона) */
      return `<span style="position:absolute;inset:0;display:flex">${items.map(item => `<img src="${esc(item.url)}" alt="" draggable="false" style="flex:1 1 0;min-width:0;height:100%;object-fit:cover;display:block">`).join('')}</span>`;
    },
    async file() {
      const f = cur();
      const order = f?.svc.get().order;
      const items = f && order?.status === 'done' ? f.gallery.items(order) : [];
      if (!f || !order || !items.length) throw new Error('result_not_ready');
      if (items.length === 1) return { data: items[0].blob, name: 'steos-collage.png' };
      if (archiveUrl(order)) return { data: await archive(order), name: 'steos-panorama.zip' };
      const zip = (window.BotCore as unknown as { zip?(files: Array<{ name: string; data: Blob }>): Promise<Blob> }).zip;
      if (!zip) throw new Error('zip_unavailable');
      return { data: await zip(items.map((item, i) => ({ name: `slide-${String(i + 1).padStart(2, '0')}.png`, data: item.blob }))), name: 'steos-panorama.zip' };
    },
    leave(go) {
      const f = cur();
      if (!f) return;
      void f.driver.leave(go, false, '');
    },
    steps: () => h.runSteps().map(([key, icon, run, done]) => [key, icon, uiText(run), uiText(done)]),
    drive(r) {
      const f = cur();
      if (f) void f.driver.drive(r);
    },
    failText(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const order = s?.order ?? null;
      const steps = tailSteps(order);
      const mod = modOf(r);
      return failText(r, {
        order,
        error: s ? s.integrityError || s.error : '',
        texts: { ...FAIL, modH: mod.h, modWhy: mod.why },
        step: steps.length ? steps[tailCurrent(order, steps)] : undefined,
      });
    },
    receipt(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const cheap = s?.intent ? !!s.intent.body.cheap : !!r.snap.cheap;
      return receipt(r, s?.order ?? null, { cheap, where: 'на коллаже' });
    },
    back() {
      const f = cur();
      if (f?.consent?.isOpen()) {
        f.consent.close();
        return true;
      }
      if (h.peeking()) {
        h.peekClose();
        return true;
      }
      if (S.sh) {
        h.sheetClose();
        return true;
      }
      if (h.set.isOpen) {
        h.set.close();
        return true;
      }
      return false;
    },
  };

  /** Подпись итога: бот прислал коллаж (у панорамы — сколько слайдов) или текст доставки (tail-done) */
  function doneText(r: BotRunRun): string {
    const f = cur() ?? frame;
    const order = f?.svc.get().order ?? null;
    const snap = r.snap as unknown as CollageSnap;
    let base = uiText('Бот уже прислал его в чат, в отдельную тему.');
    if (snap.lay === 'pano') {
      const images = (order?.result?.assets ?? []).filter(asset => /^image\//.test(asset.mime)).length;
      base = uiFormat('Бот уже прислал {0} слайда в чат, в отдельную тему. Публикуйте их каруселью по порядку.', images || h.slidesOf(snap.k));
    }
    return doneP(order, esc(base), 'cl');
  }
  /** Карточка заказа итога (runWhat шаблона на языке профиля): раскладка, фото, формат (у панорамы — слайды), фон, отступы; подпись */
  function card(r: BotRunRun): string {
    const snap = r.snap as unknown as CollageSnap;
    const L = ALL.lays.find(item => item.id === snap.lay) ?? h.layOf(snap.lay);
    const bg = ALL.bgs.find(item => item[0] === snap.bg) ?? ALL.bgs[0];
    const gap = ALL.gaps.find(item => item[0] === snap.gap) ?? ALL.gaps[2];
    const fmt = uiText((ALL.fmts.find(item => item[0] === snap.ar) ?? [snap.ar, snap.ar])[1]).toLowerCase();
    const what = [
      uiText(L.n),
      uiFormat('{0} фото', snap.k),
      snap.lay === 'pano' ? uiFormat('{0} слайда, {1}', h.slidesOf(snap.k), fmt) : fmt,
      uiFormat('фон {0}', uiText(bg[1]).toLowerCase()),
      snap.gap === '0' ? uiText('без отступов') : uiFormat('{0} отступы', uiText(gap[1]).toLowerCase()),
    ].join(' · ');
    const q = (facade as unknown as { q?(meta: string, text: string): string }).q;
    return q ? q(what, snap.text) : '';
  }

  /* ---- итог: картинка сервера ---- */
  /** Заказ готов (каждая сверка): картинки итога — один раз на заказ и ревизию; пришли — страница заказа встаёт по ним (рамка и лайтбокс) */
  function onDone(f: Frame) {
    const order = f.svc.get().order;
    if (!order || order.status !== 'done') return;
    void f.gallery.load(order).then(items => {
      if (items && cur() === f) resultPaint(f, order, items);
    });
  }
  function resultPaint(f: Frame, order: SpeechOrder, items: GalleryItem[]) {
    const r = h.tail.run;
    const key = galleryKey(order);
    if (!r || r.st !== 'done' || !h.mounted() || !items.length || f.shownKey === key + ':' + r.id) return;
    f.shownKey = key + ':' + r.id;
    h.tail.refresh();
  }
  /** «Рилс из этих фото»: первая картинка итога (у панорамы — первый слайд) после сверки владельца и заказа */
  function handoff(f: Frame, el: HTMLElement) {
    const s = f.svc.get();
    const order = s.order;
    if (!order || order.status !== 'done' || f.moving) return;
    const items = f.gallery.items(order);
    const first = items[0]?.index ?? order.result?.assets.find(asset => /^image\//.test(asset.mime))?.index;
    if (first === undefined) {
      tip(el, 'Коллаж ещё загружается');
      return;
    }
    f.moving = true;
    void busyButton(el, () =>
      handoffPhoto({ ownerId: s.identity?.owner, order, index: first, destination: 'reels', blobs: index => items.find(item => item.index === index)?.blob }),
    )
      .catch(() => {
        if (el.isConnected) tip(el, 'Не удалось передать коллаж. Откройте работу в «Истории» и попробуйте снова.');
      })
      .finally(() => {
        f.moving = false;
      });
  }

  /* ---- смена языка профиля (i18n: событие steos:locale): открытый экран рисуется заново ---- */
  /** Подписи этапов идущего заказа — строки на момент создания хвоста: им — подписи нового языка (ключи этапов те же) */
  function relabel(r: BotRunRun) {
    const next = server.steps();
    for (const step of r.steps) {
      const same = next.find(item => item[0] === step[0]);
      if (same) {
        step[2] = same[2];
        step[3] = same[3];
      }
    }
  }
  document.addEventListener('steos:locale', () => {
    const f = cur();
    const r = h.tail.run;
    if (r) relabel(r);
    /* раскладки, форматы, отступы, фоны и темы — копиями на новом языке; примеры — с новыми подписями */
    narrow(listForm);
    examplesOf = null;
    if (!f || !h.mounted()) return;
    const root = h.root();
    if (r && root) h.tail.show(root);
    else h.paintPage();
    changed(f);
  });
  /* новый снимок Me (пополнение) уже положил остаток в память кнопки (tool-helpers): подвал формы — по нему */
  document.addEventListener('steos:account-updated', () => {
    if (cur() && onForm()) h.foot.sync();
  });

  instance = { bind };
  return server;
}

/** ENGINES.collage.bind: привязка кадра к серверу экрана, созданному движком (без движка в серверном режиме — пустая привязка) */
export function bindCollageEngine(b: EngineBinding): EngineBound {
  return instance ? instance.bind(b) : { changed() {}, release() {} };
}

/** Экран «collage» реестра движков студии: движок шаблона window.BotCollage, атрибуты data-cl-*, без правой панели, знак заказа «collage» */
export const COLLAGE_ENGINE: EngineScreen = { global: 'BotCollage', prefix: 'cl', side: false, kind: 'collage', bind: bindCollageEngine };

export function registerCollageEngine() {
  ENGINES.collage = COLLAGE_ENGINE;
}
